import { Notation } from '@teetsh/app/src/types/notationSystem';

/**
 * Redistributes positive notation values equidistantly between 0 and 100.
 * Preserves the relative order of notations based on their current values.
 */
function redistributeEquidistantly(positiveNotations: Notation[]): Notation[] {
  if (positiveNotations.length === 0) {
    return [];
  }

  const sortedPositive = [...positiveNotations].sort(
    (a, b) => a.value - b.value,
  );

  const totalNotations = sortedPositive.length;
  const interval = 100 / totalNotations;

  return sortedPositive.map((notation, index) => ({
    ...notation,
    value: Math.round(interval * (index + 1)),
  }));
}

/**
 * Redistributes notation values equidistantly when adding a new notation.
 * The new notation gets value 100, and all others are redistributed equidistantly between 0 and 100.
 */
export function adjustNotationValuesForNewMax(
  existingNotations: Notation[],
  newNotation: Notation,
): Notation[] {
  const positiveNotations = existingNotations.filter(n => n.value > 0);
  const negativeNotations = existingNotations.filter(n => n.value <= 0);

  const adjustedNew = { ...newNotation, value: 100 };
  const redistributed = redistributeEquidistantly([
    ...positiveNotations,
    adjustedNew,
  ]);

  return [...negativeNotations, ...redistributed];
}

/**
 * Redistributes notation values equidistantly after removing a notation.
 * The highest remaining notation gets value 100, and all others are redistributed equidistantly.
 */
export function redistributeNotationValues(
  remainingNotations: Notation[],
): Notation[] {
  const positiveNotations = remainingNotations.filter(n => n.value > 0);
  const negativeNotations = remainingNotations.filter(n => n.value <= 0);

  if (positiveNotations.length === 0) {
    return remainingNotations;
  }

  const redistributed = redistributeEquidistantly(positiveNotations);

  return [...negativeNotations, ...redistributed];
}
